<template>
  <div class="bulk-page" :class="device.desktop ? 'pa-6' : 'pa-0'">
    <v-card class="bulk-card elevation-0">
      <div class="bulk-toolbar">
        <div class="bulk-toolbar__left">
          <div class="bulk-toolbar__icon">
            <v-icon>mdi-bullhorn-outline</v-icon>
          </div>
          <div>
            <div class="bulk-toolbar__title">Pesan Massal</div>
            <div class="bulk-toolbar__sub">
              Kirim ke banyak nomor sekaligus — pilih kontak atau import Excel
            </div>
          </div>
        </div>
        <v-btn text class="bulk-btn-ghost" to="/backend/pesan-keluar">
          Lihat Pesan Keluar
        </v-btn>
      </div>

      <div class="bulk-body">
        <div class="bulk-field-label">Nomor Pengirim</div>
        <v-select
          outlined
          dense
          hide-details="auto"
          color="#1f7a57"
          item-color="#1f7a57"
          v-model="form.sender_number"
          :items="senders"
          class="bulk-field mb-4"
          prepend-inner-icon="mdi-whatsapp"
          placeholder="Pilih nomor pengirim"
        ></v-select>

        <div class="bulk-modes mb-4">
          <button
            type="button"
            class="bulk-mode"
            :class="{ 'is-active': mode === 'contacts' }"
            @click="mode = 'contacts'"
          >
            <v-icon small class="mr-1">mdi-account-multiple</v-icon>
            Pilih Kontak
          </button>
          <button
            type="button"
            class="bulk-mode"
            :class="{ 'is-active': mode === 'excel' }"
            @click="mode = 'excel'"
          >
            <v-icon small class="mr-1">mdi-file-excel</v-icon>
            Import Excel
          </button>
        </div>

        <template v-if="mode === 'contacts'">
          <div class="bulk-field-label">
            Penerima
            <span class="bulk-count" v-if="selectedPhones.length">
              {{ selectedPhones.length }} dipilih
            </span>
          </div>
          <v-autocomplete
            outlined
            dense
            hide-details="auto"
            color="#1f7a57"
            item-color="#1f7a57"
            v-model="selectedPhones"
            :items="contacts"
            multiple
            chips
            small-chips
            deletable-chips
            class="bulk-field mb-2"
            prepend-inner-icon="mdi-account-outline"
            placeholder="Cari & pilih kontak"
          ></v-autocomplete>
          <div class="bulk-actions-row mb-4">
            <v-btn text small class="bulk-btn-ghost" @click="selectAllContacts">
              Pilih semua
            </v-btn>
            <v-btn text small class="bulk-btn-ghost" @click="selectedPhones = []">
              Kosongkan
            </v-btn>
          </div>
        </template>

        <template v-else>
          <div class="bulk-field-label">File Excel / CSV</div>
          <div class="bulk-import-box mb-3">
            <input
              ref="excelInput"
              type="file"
              accept=".xlsx,.xls,.csv"
              class="bulk-file-input"
              @change="onExcelSelected"
            />
            <v-btn outlined class="bulk-btn-upload" @click="$refs.excelInput.click()">
              <v-icon left small>mdi-upload</v-icon>
              {{ excelRows.length ? 'Ganti File' : 'Pilih File' }}
            </v-btn>
            <div class="bulk-hint">
              Kolom A = nomor, kolom B = pesan (opsional). Maks. 100 baris.
              <a href="#" class="bulk-link" @click.prevent="downloadTemplate">
                Unduh template
              </a>
            </div>
          </div>
          <div v-if="excelRows.length" class="bulk-preview mb-4">
            <div class="bulk-preview__head">
              Preview {{ excelRows.length }} baris
            </div>
            <v-simple-table dense class="bulk-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Nomor</th>
                  <th>Pesan</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, i) in excelRows.slice(0, 20)" :key="i">
                  <td>{{ i + 1 }}</td>
                  <td>{{ row.recieve_number }}</td>
                  <td>{{ row.content || '(pakai pesan bersama)' }}</td>
                </tr>
              </tbody>
            </v-simple-table>
            <div v-if="excelRows.length > 20" class="bulk-hint pa-2">
              …dan {{ excelRows.length - 20 }} baris lainnya
            </div>
          </div>
        </template>

        <v-row dense class="mb-2">
          <v-col cols="12" sm="6">
            <div class="bulk-field-label">Tipe Pesan</div>
            <v-select
              outlined
              dense
              hide-details="auto"
              color="#1f7a57"
              item-color="#1f7a57"
              v-model="form.type"
              :items="messageTypes"
              class="bulk-field"
              @change="onTypeChange"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="6">
            <div class="bulk-field-label">Waktu Pengiriman</div>
            <v-select
              outlined
              dense
              hide-details="auto"
              color="#1f7a57"
              item-color="#1f7a57"
              v-model="form.process"
              :items="processes"
              class="bulk-field"
            ></v-select>
          </v-col>
        </v-row>

        <template v-if="form.type === 'image'">
          <div class="bulk-field-label">Gambar (sama untuk semua penerima)</div>
          <div class="bulk-media-box mb-4">
            <div v-if="form.media_url" class="bulk-media-preview">
              <img :src="form.media_url" alt="preview" />
              <v-btn icon small class="bulk-media-remove" @click="clearMedia">
                <v-icon small>mdi-close</v-icon>
              </v-btn>
            </div>
            <div v-else class="bulk-media-empty">
              <v-icon large color="#1f7a57">mdi-image-outline</v-icon>
              <div>Belum ada gambar</div>
            </div>
            <v-btn
              outlined
              class="bulk-btn-upload mt-3"
              :loading="uploadingMedia"
              @click="uploadImage"
            >
              <v-icon left small>mdi-upload</v-icon>
              Unggah Gambar
            </v-btn>
          </div>
        </template>

        <template v-if="form.type === 'document'">
          <div class="bulk-field-label">Dokumen (sama untuk semua penerima)</div>
          <div class="bulk-media-box mb-4">
            <div v-if="form.media" class="bulk-doc-selected">
              <v-icon color="#1f7a57" class="mr-2">mdi-file-document-outline</v-icon>
              {{ form.media }}
              <v-btn icon small @click="clearMedia">
                <v-icon small>mdi-close</v-icon>
              </v-btn>
            </div>
            <div v-else class="bulk-media-empty">
              <v-icon large color="#1f7a57">mdi-file-upload-outline</v-icon>
              <div>Belum ada dokumen</div>
            </div>
            <v-btn
              outlined
              class="bulk-btn-upload mt-3"
              :loading="uploadingMedia"
              @click="uploadDocument"
            >
              <v-icon left small>mdi-upload</v-icon>
              Unggah Dokumen
            </v-btn>
          </div>
        </template>

        <div class="bulk-field-label">
          {{
            form.type === 'text'
              ? mode === 'excel'
                ? 'Pesan bersama (opsional jika isi kolom B)'
                : 'Pesan'
              : 'Caption (opsional)'
          }}
        </div>
        <v-textarea
          outlined
          dense
          color="#1f7a57"
          hide-details="auto"
          v-model="form.content"
          rows="3"
          auto-grow
          class="bulk-field mb-4"
          :placeholder="
            form.type === 'text'
              ? 'Tulis pesan WhatsApp...'
              : 'Caption untuk media (opsional)'
          "
        ></v-textarea>

        <div v-if="form.process === 'schedule'" class="mb-4">
          <div class="bulk-field-label">Waktu jadwal</div>
          <v-text-field
            outlined
            dense
            type="datetime-local"
            color="#1f7a57"
            hide-details="auto"
            v-model="form.scheduled_at"
            class="bulk-field"
          ></v-text-field>
        </div>

        <div v-if="lastResult" class="bulk-result mb-4">
          <div class="bulk-result__title">{{ lastResult.message }}</div>
          <div class="bulk-result__meta">
            Berhasil {{ lastResult.data.success }} · Gagal
            {{ lastResult.data.failed }} · Total {{ lastResult.data.total }}
          </div>
        </div>
      </div>

      <div class="bulk-footer">
        <div class="bulk-footer__hint">
          {{ itemCount }} penerima siap dikirim (maks. 100)
        </div>
        <v-spacer></v-spacer>
        <v-btn
          class="bulk-btn-primary"
          :loading="sending"
          :disabled="sending || !itemCount"
          @click="submitBulk"
        >
          <v-icon left small>mdi-send</v-icon>
          Kirim Massal
        </v-btn>
      </div>
    </v-card>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import XLSX from "xlsx";

export default {
  name: "PesanMassal",
  data: () => ({
    mode: "contacts",
    senders: [],
    contacts: [],
    selectedPhones: [],
    excelRows: [],
    uploadingMedia: false,
    sending: false,
    lastResult: null,
    form: {
      sender_number: null,
      type: "text",
      content: "",
      media: null,
      media_url: null,
      process: "now",
      scheduled_at: null,
    },
    processes: [
      { text: "Langsung", value: "now" },
      { text: "Terjadwal", value: "schedule" },
    ],
  }),
  computed: {
    ...mapState(["page", "theme", "http", "device", "snackbar", "user"]),
    messageTypes() {
      const p = this.user && this.user.paket;
      if (!p || p.unlimited) {
        return [
          { text: "Text", value: "text" },
          { text: "Gambar", value: "image" },
          { text: "Dokumen", value: "document" },
        ];
      }
      const items = [];
      if (p.allow_text) items.push({ text: "Text", value: "text" });
      if (p.allow_image) items.push({ text: "Gambar", value: "image" });
      if (p.allow_document) items.push({ text: "Dokumen", value: "document" });
      return items.length
        ? items
        : [{ text: "Text", value: "text" }];
    },
    itemCount() {
      return this.mode === "contacts"
        ? this.selectedPhones.length
        : this.excelRows.length;
    },
  },
  created() {
    this.setPage({
      crud: false,
      dataUrl: "api/pesan/outbox",
      title: "PESAN MASSAL",
      subtitle: "Kirim pesan ke banyak penerima",
      showtable: false,
      breadcrumbs: [
        { text: "Pesan", disabled: true, href: "" },
        { text: "Pesan Massal", disabled: false, href: "" },
      ],
      actions: {
        refresh: false,
        add: false,
        edit: false,
        delete: false,
      },
    });
    this.bootstrap();
  },
  methods: {
    ...mapActions(["setPage", "assignFileBrowse"]),
    async bootstrap() {
      try {
        const [senders, contacts] = await Promise.all([
          this.http.get("api/combo/pengaturan"),
          this.http.get("api/combo/contact"),
        ]);
        this.senders = senders.data || [];
        this.contacts = contacts.data || [];
        if (this.senders.length === 1) {
          this.form.sender_number = this.senders[0].value;
        }
        if (
          this.messageTypes.length &&
          !this.messageTypes.find((t) => t.value === this.form.type)
        ) {
          this.form.type = this.messageTypes[0].value;
        }
        const soon = new Date(Date.now() + 10 * 60 * 1000);
        this.form.scheduled_at = this.toLocalInputValue(soon);
      } catch (e) {
        this.snackbar.color = "red";
        this.snackbar.text = "Gagal memuat data pengirim/kontak";
        this.snackbar.state = true;
      }
    },
    toLocalInputValue(date) {
      const pad = (n) => String(n).padStart(2, "0");
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
        date.getDate()
      )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
    },
    selectAllContacts() {
      this.selectedPhones = (this.contacts || []).map((c) => c.value);
    },
    onTypeChange() {
      this.clearMedia();
    },
    clearMedia() {
      this.form.media = null;
      this.form.media_url = null;
    },
    uploadImage() {
      this.assignFileBrowse({
        fileType: [".jpg", ".jpeg", ".png", ".webp"],
        maxSize: 5000000,
        query: { doctype: "messages" },
        callback: (response) => {
          this.uploadingMedia = false;
          if (!response || !response.name) {
            this.snackbar.color = "red";
            this.snackbar.text = "Gagal mengunggah gambar";
            this.snackbar.state = true;
            return;
          }
          this.form.media = response.name;
          this.form.media_url = response.path;
        },
      });
      this.uploadingMedia = true;
      setTimeout(() => {
        this.uploadingMedia = false;
      }, 8000);
    },
    uploadDocument() {
      this.assignFileBrowse({
        fileType: [
          ".pdf",
          ".doc",
          ".docx",
          ".xls",
          ".xlsx",
          ".ppt",
          ".pptx",
          ".txt",
          ".csv",
          ".zip",
        ],
        maxSize: 15000000,
        query: { doctype: "documents" },
        callback: (response) => {
          this.uploadingMedia = false;
          if (!response || !response.name) {
            this.snackbar.color = "red";
            this.snackbar.text = "Gagal mengunggah dokumen";
            this.snackbar.state = true;
            return;
          }
          this.form.media = response.name;
          this.form.media_url = response.path;
        },
      });
      this.uploadingMedia = true;
      setTimeout(() => {
        this.uploadingMedia = false;
      }, 12000);
    },
    normalizePhone(raw) {
      let digits = String(raw || "").replace(/\D/g, "");
      if (!digits) return "";
      if (digits.startsWith("0")) digits = "62" + digits.slice(1);
      return digits;
    },
    onExcelSelected(event) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const wb = XLSX.read(data, { type: "array" });
          const sheet = wb.Sheets[wb.SheetNames[0]];
          const rows = XLSX.utils.sheet_to_json(sheet, {
            header: 1,
            defval: "",
          });
          const parsed = [];
          for (let i = 0; i < rows.length; i++) {
            const row = rows[i] || [];
            const phoneRaw = row[0];
            const msgRaw = row[1];
            // skip header-like
            const phoneStr = String(phoneRaw || "").trim().toLowerCase();
            if (
              !phoneStr ||
              phoneStr === "nomor" ||
              phoneStr === "phone" ||
              phoneStr === "recieve_number"
            ) {
              continue;
            }
            const phone = this.normalizePhone(phoneRaw);
            if (!phone || phone.length < 9) continue;
            const content = String(msgRaw || "").trim();
            parsed.push({
              recieve_number: phone,
              content: content || undefined,
            });
            if (parsed.length >= 100) break;
          }
          this.excelRows = parsed;
          if (!parsed.length) {
            this.snackbar.color = "orange";
            this.snackbar.text = "Tidak ada baris valid di file";
            this.snackbar.state = true;
          }
        } catch (err) {
          this.snackbar.color = "red";
          this.snackbar.text = "Gagal membaca file Excel";
          this.snackbar.state = true;
          this.excelRows = [];
        }
      };
      reader.readAsArrayBuffer(file);
      event.target.value = "";
    },
    downloadTemplate() {
      const ws = XLSX.utils.aoa_to_sheet([
        ["nomor", "pesan"],
        ["6281234567890", "Halo, ini pesan massal"],
        ["6281987654321", ""],
      ]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Penerima");
      XLSX.writeFile(wb, "template-pesan-massal.xlsx");
    },
    buildItems() {
      if (this.mode === "contacts") {
        return this.selectedPhones.map((phone) => ({
          recieve_number: this.normalizePhone(phone),
        }));
      }
      return this.excelRows;
    },
    async submitBulk() {
      if (!this.form.sender_number) {
        this.snackbar.color = "orange";
        this.snackbar.text = "Pilih nomor pengirim";
        this.snackbar.state = true;
        return;
      }
      const items = this.buildItems().filter((i) => i.recieve_number);
      if (!items.length) {
        this.snackbar.color = "orange";
        this.snackbar.text = "Belum ada penerima";
        this.snackbar.state = true;
        return;
      }
      if (items.length > 100) {
        this.snackbar.color = "orange";
        this.snackbar.text = "Maksimal 100 penerima";
        this.snackbar.state = true;
        return;
      }
      if (this.form.type === "image" && !this.form.media) {
        this.snackbar.color = "orange";
        this.snackbar.text = "Unggah gambar terlebih dahulu";
        this.snackbar.state = true;
        return;
      }
      if (this.form.type === "document" && !this.form.media) {
        this.snackbar.color = "orange";
        this.snackbar.text = "Unggah dokumen terlebih dahulu";
        this.snackbar.state = true;
        return;
      }
      const hasShared = !!(this.form.content || "").trim();
      const excelNeedsMsg =
        this.mode === "excel" &&
        this.form.type === "text" &&
        !hasShared &&
        items.some((i) => !(i.content || "").trim());
      if (this.form.type === "text" && this.mode === "contacts" && !hasShared) {
        this.snackbar.color = "orange";
        this.snackbar.text = "Isi pesan terlebih dahulu";
        this.snackbar.state = true;
        return;
      }
      if (excelNeedsMsg) {
        this.snackbar.color = "orange";
        this.snackbar.text =
          "Isi pesan bersama atau kolom pesan di setiap baris Excel";
        this.snackbar.state = true;
        return;
      }
      if (this.form.process === "schedule" && !this.form.scheduled_at) {
        this.snackbar.color = "orange";
        this.snackbar.text = "Pilih waktu jadwal";
        this.snackbar.state = true;
        return;
      }

      const ok = await this.$confirm({
        title: "Kirim Pesan Massal",
        text: `Kirim ke ${items.length} penerima sekarang?`,
        confirmText: "Ya, Kirim",
        cancelText: "Batal",
      });
      if (!ok) return;

      this.sending = true;
      this.lastResult = null;
      try {
        const { data } = await this.http.post("api/pesan/outbox-bulk", {
          sender_number: this.form.sender_number,
          type: this.form.type,
          content: this.form.content || "",
          media:
            this.form.type === "image" || this.form.type === "document"
              ? this.form.media
              : null,
          process: this.form.process,
          scheduled_at:
            this.form.process === "schedule"
              ? new Date(this.form.scheduled_at).toISOString()
              : null,
          schedule_type: this.form.process === "schedule" ? "once" : null,
          items,
        });
        this.lastResult = data;
        this.snackbar.color = data.success ? "green" : "orange";
        this.snackbar.text = data.message || "Selesai";
        this.snackbar.state = true;
      } catch (e) {
        this.snackbar.color = "red";
        this.snackbar.text =
          (e.response && e.response.data && e.response.data.message) ||
          "Gagal mengirim massal";
        this.snackbar.state = true;
      } finally {
        this.sending = false;
      }
    },
  },
};
</script>

<style scoped>
.bulk-page {
  --gw-ink: #10241c;
  --gw-forest: #0d4a36;
  --gw-leaf: #1f7a57;
  --gw-mint: #8fd6b5;
  --gw-fog: #eef6f1;
  --gw-line: #c9ddd3;
  --gw-muted: #5d7268;
}

.bulk-card {
  border-radius: 14px !important;
  overflow: hidden;
  border: 1px solid var(--gw-line);
  background: #fff !important;
  max-width: 720px;
}

.bulk-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.15rem;
  background:
    radial-gradient(120% 80% at 0% 0%, #1a6b4d 0%, transparent 55%),
    linear-gradient(175deg, #0b3d2e 0%, #0f5c45 45%, #0a3327 100%);
  color: #f4fbf7;
}

.bulk-toolbar__left {
  display: flex;
  align-items: center;
  gap: 0.85rem;
}

.bulk-toolbar__icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  background: linear-gradient(145deg, #b7ebcf, #8fd6b5);
}

.bulk-toolbar__icon .v-icon {
  color: #0b3d2e !important;
}

.bulk-toolbar__title {
  font-family: "Outfit", "Montserrat", sans-serif;
  font-weight: 700;
  font-size: 1.05rem;
}

.bulk-toolbar__sub {
  margin-top: 0.15rem;
  font-size: 0.75rem;
  color: rgba(244, 251, 247, 0.65);
}

.bulk-body {
  padding: 1.25rem;
}

.bulk-field-label {
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--gw-forest);
  margin-bottom: 0.35rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.bulk-count {
  font-size: 0.7rem;
  font-weight: 700;
  padding: 0.15rem 0.45rem;
  border-radius: 6px;
  background: rgba(143, 214, 181, 0.3);
  color: var(--gw-forest);
}

.bulk-field >>> .v-input__slot {
  border-radius: 10px !important;
}

.bulk-modes {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
}

.bulk-mode {
  border: 1px solid var(--gw-line);
  background: #fff;
  border-radius: 10px;
  padding: 0.7rem 0.85rem;
  font-weight: 700;
  font-size: 0.88rem;
  color: var(--gw-muted);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.bulk-mode.is-active {
  border-color: var(--gw-leaf);
  background: var(--gw-fog);
  color: var(--gw-forest);
}

.bulk-actions-row {
  display: flex;
  gap: 0.25rem;
}

.bulk-import-box,
.bulk-media-box {
  padding: 1rem;
  border-radius: 10px;
  border: 1px dashed var(--gw-line);
  background: var(--gw-fog);
}

.bulk-file-input {
  display: none;
}

.bulk-hint {
  margin-top: 0.55rem;
  font-size: 0.78rem;
  color: var(--gw-muted);
  line-height: 1.45;
}

.bulk-link {
  color: var(--gw-leaf);
  font-weight: 700;
  text-decoration: none;
}

.bulk-preview {
  border: 1px solid var(--gw-line);
  border-radius: 10px;
  overflow: hidden;
}

.bulk-preview__head {
  padding: 0.55rem 0.85rem;
  background: var(--gw-fog);
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--gw-forest);
}

.bulk-table {
  background: transparent !important;
}

.bulk-media-empty {
  text-align: center;
  color: var(--gw-muted);
  font-size: 0.85rem;
  padding: 0.75rem;
}

.bulk-media-preview {
  position: relative;
  display: inline-block;
}

.bulk-media-preview img {
  max-width: 180px;
  max-height: 140px;
  border-radius: 8px;
  border: 1px solid var(--gw-line);
}

.bulk-media-remove {
  position: absolute !important;
  top: 4px;
  right: 4px;
  background: #fff !important;
}

.bulk-doc-selected {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-weight: 600;
  font-size: 0.88rem;
  color: var(--gw-ink);
  word-break: break-all;
}

.bulk-result {
  padding: 0.85rem 1rem;
  border-radius: 10px;
  background: var(--gw-fog);
  border: 1px solid var(--gw-line);
}

.bulk-result__title {
  font-weight: 700;
  color: var(--gw-forest);
  font-size: 0.92rem;
}

.bulk-result__meta {
  margin-top: 0.25rem;
  font-size: 0.8rem;
  color: var(--gw-muted);
}

.bulk-footer {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem 1.25rem;
  border-top: 1px solid var(--gw-line);
}

.bulk-footer__hint {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--gw-muted);
}

.bulk-btn-primary {
  background: linear-gradient(145deg, #b7ebcf, #8fd6b5) !important;
  color: #0b3d2e !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 700 !important;
  border-radius: 10px !important;
  box-shadow: none !important;
}

.bulk-btn-ghost {
  text-transform: none !important;
  letter-spacing: 0 !important;
  color: var(--gw-leaf) !important;
  font-weight: 700 !important;
}

.bulk-toolbar .bulk-btn-ghost {
  color: rgba(244, 251, 247, 0.85) !important;
}

.bulk-btn-upload {
  text-transform: none !important;
  letter-spacing: 0 !important;
  color: var(--gw-forest) !important;
  border-color: var(--gw-line) !important;
  font-weight: 700 !important;
  border-radius: 10px !important;
}

@media (max-width: 600px) {
  .bulk-footer {
    flex-direction: column;
    align-items: stretch;
  }
  .bulk-toolbar {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
